import React, { useState, useEffect, useCallback} from 'react';
import { styled } from '@mui/material/styles';
import scStyled from 'styled-components';
import { Avatar, Box, Typography, ToggleButtonGroup, ToggleButton, Tabs, Tab, Grid, List, ListItem, ListItemText, ListItemIcon, Checkbox, IconButton, Snackbar, Alert, CircularProgress, RadioGroup, FormControlLabel, Radio, useMediaQuery } from '@mui/material';
import moment from 'moment';
import { useAppSelector, useAppDispatch, fetchRooms, selectRooms, fetchTypes, selectTypes, createReservation, selectOperationLoading, selectOperationSuccess, selectOperationError, resetOperationStatus, selectAuth, updateReservation, selectReservations, selectPreloadData, fetchMessages, selectMessages, deleteReservation } from '@redux';
import { ReservationsParticipant, Reservation } from 'src/types';
import { useHistory } from 'react-router-dom';
import { AppCustomeSelect, AppTextArea, AppInput } from '../Common';
import { PassChangeButton } from '../Profile';
import { useMessageOperations } from 'src/Utils/Hooks/useScheduleMessages';

import { ParticipantsSelector } from './ParticipantsSelector';
import { ReactComponent as IconFileImage } from '@assets/Icons/IconFileImage.svg';
import { ReactComponent as IconFileFile } from '@assets/Icons/IconFileFile.svg';
import { ReactComponent as IconPlus } from '@assets/Icons/IconPlus.svg';
import { findReservationConflicts } from './findReservationConflicts';


// Component props interface
interface ReservationFormProps {
  isModal?: boolean;
  onClose?: () => void;
  initialData?: {
    reservation_id?: number;
    start_date?: string;
    start_time?: string;
    end_time?: string;
    end_date?: string;
    facility_id?: number;
  };
  defaultParticipantIds?: number[];
  refreshReservations: () => void;
}

// Tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Room type interface
interface RoomType {
  id: number;
  name: string;
  capacity: number;
}

// TabPanel component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { 
          xs: '8px', 
          md: 2
        } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Main component
export const ReservationForm: React.FC<ReservationFormProps> = ({ 
  isModal = false, 
  onClose, 
  initialData = {},
  refreshReservations,
}) => {
  const isMobileLayout = useMediaQuery(`(max-width: 768px)`);
  const radioLabelPlacement = isMobileLayout ? "bottom" : "end";

  const history = useHistory();
  const dispatch = useAppDispatch();
  const { createMessage, updateMessage, deleteMessage, requestFileUrl } = useMessageOperations();
  
  // Get current user
  const auth = useAppSelector(selectAuth);
  const currentUser = auth.userInfo;

  // Get all users
  const preloadData = useAppSelector(selectPreloadData);
  const users = preloadData.users;
  const groups = preloadData.groups;

  // Get rooms and types from Redux
  const reservations = useAppSelector(selectReservations);
  const rooms = useAppSelector(selectRooms);
  const types = useAppSelector(selectTypes);
  const originalReservation = reservations.data.find(reservation => reservation.id === initialData.reservation_id) || null;
  const originalReservationType = originalReservation?.recurring_frequency ? 'recurring' : originalReservation?.end_date !== originalReservation?.start_date ? 'multiday' : originalReservation ? 'normal' : null;
  const messages = useAppSelector(selectMessages);
  //console.log("messages", messages);
  
  // Operation state
  const loading = useAppSelector(selectOperationLoading);
  const success = useAppSelector(selectOperationSuccess);
  const error = useAppSelector(selectOperationError);
  
  // Form state
  const [reservationType, setReservationType] = useState<'normal' | 'multiday' | 'recurring'>(originalReservationType || 'normal');
  const [selectedParticipants, setSelectedParticipants] = useState<ReservationsParticipant[]>(originalReservation?.participants || []);
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [formData, setFormData] = useState<Reservation>(originalReservation || {
    id: null,
    room_ids: [],
    type_id: 0,
    title: '',
    description: '',
    start_date: initialData.start_date || moment().add(1, 'day').format('YYYY-MM-DD'),
    end_date: initialData.end_date || initialData.start_date || moment().add(8, 'days').format('YYYY-MM-DD'),
    start_time: initialData.start_time || '09:00',
    end_time: initialData.end_time || '10:00',

    recurring_frequency: null, // 'daily', 'weekly', 'monthly', 'yearly'
    recurring_occurences: 1, // for recurring reservations, how many times it occurs
    recurring_gap: 1,
    days_of_week: [(new Date().getDay() + 7) % 7],
    recurring_date: initialData?.start_date ? Number(moment(initialData.start_date).format('DD')) : 1, // for monthly or yearly recurring reservations, which date of the month it occurs on
    week_number: 1, // for monthly or yearly recurring reservations, which week of the month it occurs in (1 = first week, 2 = second week, -1 = final week) 
    day_of_week: 1, // for monthly or yearly recurring reservations, which day of the week it occurs on (1 = Monday, ..., 6 = Saturday, 7 = Sunday)
    recurring_month: 1, // for yearly recurring reservations, what month it occurs in

    is_private: false,
    participants: [],
    color: 'white',
  });
    
  // Rooms state
  const [selectedRooms, setSelectedRooms] = useState<RoomType[]>([]);
  const [availableRooms, setAvailableRooms] = useState<RoomType[]>([]);
  const [unavailableRoomIds, setUnavailableRoomIds] = useState<number[]>([]);
  const [selectedAvailableRooms, setSelectedAvailableRooms] = useState<number[]>([]);
  const [selectedCurrentRooms, setSelectedCurrentRooms] = useState<number[]>([]);

  // Files state
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadedFilePaths, setUploadedFilePaths] = useState<string[]>([]);
  const [tempReservationId, setTempReservationId] = useState<number>(Math.floor(Math.random() * 2_000_000) + 10_000);

  // Notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'error'>('success');
  
// Related to the radio group for end date
  const [recurringEndOption, setRecurringEndOption] = useState<'forever' | 'occurrences' | 'specific_date'>('occurrences');
  
  // Initialize rooms amd types when component mounts or rooms data changes
  useEffect(() => {
    if (rooms?.data) {
      // Set initial selected rooms from the reservation if it exists
      const roomIds = originalReservation?.room_ids || (initialData.facility_id ? [initialData.facility_id] : []);
      
      // Find the room objects that match the IDs in room_ids
      const initialSelectedRooms = rooms.data.filter(room => 
        Array.isArray(roomIds) && roomIds.includes(room.id)
      );
      
      // Set the selected rooms
      setSelectedRooms(initialSelectedRooms);
      
      // Create a Set of selected room IDs to exclude from available rooms
      const selectedRoomIds = new Set(initialSelectedRooms.map(room => room.id));
      
      // Set available rooms (all rooms that aren't selected)
      const initialAvailableRooms = rooms.data.filter(room => !selectedRoomIds.has(room.id));
      setAvailableRooms(initialAvailableRooms);
    }
    if (types?.data) {
      const initialType = types.data.find(type => type.id === formData.type_id);
      const typeColor = initialType?.color || '#FFFFFF'; // Default to white if no type found
      setFormData(prev => ({
        ...prev,
        color: typeColor
      }));
    }
    if (originalReservation && originalReservation.recurring_frequency) {
      const reservationOccurences = calculateOccurrences(originalReservation.start_date, originalReservation.end_date, originalReservation.recurring_frequency || "", originalReservation.recurring_gap || 1, originalReservation.week_number, originalReservation.day_of_week, originalReservation.recurring_month, originalReservation.days_of_week);
      setFormData(prev => ({
        ...prev,
        recurring_occurences: reservationOccurences
      }));
    }
  }, [rooms, originalReservation, types]);

  const checkRoomAvailability = useCallback(() => {
    // if "readyToSubmit" is true, we are already submitting the form, so another check might check the new reservation (locally) against the new reservation (already saved on the server)
    if (!reservations || !formData.start_date || !formData.start_time || !formData.end_time || readyToSubmit) {
      return;
    }

    const conflictingReservations = findReservationConflicts(reservations.data, formData);
    //console.log("conflictingReservations", conflictingReservations);
    const unavailableRooms = [...new Set(
      conflictingReservations.flatMap((reservationId) => {
        const reservation = reservations.data.find(res => res.id === reservationId);
        return reservation?.room_ids || [];
      })
    )];
    //console.log("unavailableRooms", unavailableRooms);

    // This way didn't account for new reservations that were recurring
//    const events = createScheduleEvents(reservations.data, new Date(formData.start_date), types.data);
//
//    // Parse the planned reservation date and time
//    const startDateTime = moment(`${formData.start_date} ${formData.start_time}`);
//    const endDateTime = moment(`${formData.end_date || formData.start_date} ${formData.end_time}`);
//    
//    // Find unavailable rooms
//    const unavailableRooms: number[] = [];
//
//    events.forEach(event => {
//      if(event.reservation?.id === formData.id) {
//        return; // Skip the current reservation being edited
//      }
//      const eventStart = moment(event.startDate);
//      const eventEnd = moment(event.endDate);
//      const isOverlapping = startDateTime.isBefore(eventEnd) && endDateTime.isAfter(eventStart);
//      if (isOverlapping) {
//        // Add all rooms from this event to unavailable list
//        event.reservation?.room_ids.forEach(roomId => {
//          if (!unavailableRooms.includes(roomId)) {
//            unavailableRooms.push(roomId);
//          }
//        });
//      }
//    });
//
    setUnavailableRoomIds(unavailableRooms);

    const roomsToRemove = selectedRooms.filter(room => unavailableRooms.includes(room.id));
    //console.log("roomsToRemove", roomsToRemove);
    if (roomsToRemove.length === 0) { return; }

    const stillAvailableRooms = selectedRooms.filter(room => !unavailableRooms.includes(room.id));
    setSelectedRooms(stillAvailableRooms);

    // Update the list of rooms that aren't selected (i.e. on the right)
    setAvailableRooms(prevAvailable => {
      const newAvailable = [...prevAvailable, ...roomsToRemove].sort((a, b) => a.id - b.id);
      return newAvailable;
    });

    // Save the selected rooms that are available at this time
    setFormData(prevFormData => ({
      ...prevFormData,
      room_ids: stillAvailableRooms.map(room => room.id)
    }));

    // If moving around rooms, unselect them all
    setSelectedCurrentRooms([]);
    setSelectedAvailableRooms([]);

    const roomNames = roomsToRemove.map(room => room.name).join(', ');
    setNotificationMessage(`選択した部屋のうち、以下は予約できません: ${roomNames}`);
    setNotificationType('error');
    setShowNotification(true);
  }, [reservations, formData.start_date, formData.end_date, formData.start_time, formData.end_time, formData.id, selectedRooms, types.data]);
  
  // Add useEffect to check availability when date/time changes
  useEffect(() => {
    checkRoomAvailability();
  }, [formData.start_date, formData.end_date, formData.start_time, formData.end_time, checkRoomAvailability]);

  // Fetch rooms and types on component mount
  useEffect(() => {
    dispatch(fetchRooms());
    dispatch(fetchTypes());
    dispatch(fetchMessages());
  }, [dispatch]);
  
  // Reset operation status when unmounting
  useEffect(() => {
    return () => {
      dispatch(resetOperationStatus());
    };
  }, [dispatch]);
  
  // Handle success or error from reservation creation
  useEffect(() => {
    if (!readyToSubmit) { return; }

    setTempReservationId(Math.floor(Math.random() * 2_000_000) + 10_000);
    if (success) {
      setNotificationMessage('予約が正常に作成されました');
      setNotificationType('success');
      setShowNotification(true);
      
      // Clear form if it's a modal
      // TODO this should be cleared if the modal is opened again
      if (isModal) {
        setTimeout(() => {
          onClose && onClose();
        }, 1500);
      } else {
        resetForm();
      }
      
      dispatch(resetOperationStatus());
    }
    
    if (error) {
      setNotificationMessage(prev => prev ? prev : `エラーが発生しました: ${error}`);
      setNotificationType('error');
      setShowNotification(true);
      dispatch(resetOperationStatus());
    }
  }, [readyToSubmit, success, error, isModal, onClose, dispatch]);
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {

    // If this is an existing reservation, don't allow changing between regular/multiday/recurring
    if(originalReservationType !== null) { return; }

    setReservationType(newValue === 0 ? 'normal' : newValue === 1 ? 'multiday' : 'recurring');
    
    // If switching to multiday, ensure end_date is set properly
    if (newValue === 1) {
      // If end_date is same as start_date, set it to next day by default
      if (formData.end_date === formData.start_date) {
        const nextDay = moment(formData.start_date).add(1, 'day').format('YYYY-MM-DD');
        setFormData({
          ...formData,
          end_date: nextDay
        });
      }
    }
    
    // If switching to recurring, reset the recurring frequency
    if (newValue === 2) {
      setFormData({
        ...formData,
        recurring_frequency: ''
      });
    } else {
      // Clear recurring frequency if not on recurring tab
      setFormData({
        ...formData,
        recurring_frequency: ''
      });
    }
  };
  
  // Handle form input changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle select changes
  const handleSelectChange = (values: any, name) => {
    const { value } = values;
    setFormData({
      ...formData,
      [name]: Number(value)
    });
  };

  const changeColorToDefault = (value: any) => {
    console.log("changeColorToDefault", value);
    const newType = types.data.find(type => type.id === value);
    const newColor = newType?.color;
    console.log("newType color", newColor);
    if (!newColor) { return; }
    setFormData((prev) => ({
      ...prev,
      type_id: value,
      color: newColor
    }));
  };

  // Handle public/private toggle
  const handlePublicChange = (event, newAlignment) => {
    if (newAlignment !== null) {
      setFormData((prev) => ({
        ...prev,
        is_private: newAlignment === 'private',
      }));
    }
  };
    
  // Handle adding rooms
  const handleAddRooms = () => {
    if (selectedAvailableRooms.length === 0) return;
    
    const newSelectedRooms = [...selectedRooms];
    const newAvailableRooms = [...availableRooms];

    const availableSelectedRooms = selectedAvailableRooms.filter(
      roomId => !unavailableRoomIds.includes(roomId)
    );

    // Add selected available rooms to selected rooms
    availableSelectedRooms.forEach(roomId => {
      const roomIndex = newAvailableRooms.findIndex(room => room.id === roomId);
      if (roomIndex !== -1) {
        const room = newAvailableRooms[roomIndex];
        newSelectedRooms.push(room);
        newAvailableRooms.splice(roomIndex, 1);
      }
    });
    
    setSelectedRooms(newSelectedRooms);
    setAvailableRooms(newAvailableRooms);
    setSelectedAvailableRooms([]);
    
    // Update formData with new room_ids
    setFormData({
      ...formData,
      room_ids: newSelectedRooms.map(room => room.id)
    });
  };
  
  // Handle removing rooms
  const handleRemoveRooms = () => {
    if (selectedCurrentRooms.length === 0) return;
    
    const newSelectedRooms = [...selectedRooms];
    const newAvailableRooms = [...availableRooms];
    
    // Move selected rooms back to available rooms
    selectedCurrentRooms.forEach(roomId => {
      const roomIndex = newSelectedRooms.findIndex(room => room.id === roomId);
      if (roomIndex !== -1) {
        const room = newSelectedRooms[roomIndex];
        newAvailableRooms.push(room);
        newSelectedRooms.splice(roomIndex, 1);
      }
    });
    
    setSelectedRooms(newSelectedRooms);
    setAvailableRooms(newAvailableRooms);
    setSelectedCurrentRooms([]);
    
    // Update formData with new room_ids
    setFormData({
      ...formData,
      room_ids: newSelectedRooms.map(room => room.id)
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) { return; }

    setUploadingFiles(true);

    const response = await requestFileUrl({
      filename: file.name,
      file_type: file.type,
      acl: 'public-read'
    });

    // Extract the presigned URL and other data
    const { presigned_url, file_path, file_url, original_filename, disposition_filename } = response.payload.data;
    
    console.log("Got presigned URL:", presigned_url);

    setUploadedFilePaths(prev => [...prev, file_path]);

    const uploadResponse = await fetch(presigned_url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'x-amz-acl': 'public-read',
        //'Content-Disposition': `attachment; filename="${disposition_filename || original_filename || file.name}"`
        'Content-Disposition': `attachment; filename*=UTF-8''${encodeURIComponent(disposition_filename || original_filename || file.name)}`
      },
      body: file
    });

    if (!uploadResponse.ok) {
      setUploadingFiles(false);
      throw new Error(`Upload failed with status: ${uploadResponse.status}`);
    }

    console.log("File uploaded successfully");

    // Create a message with the file URL
    // If this is a new (not yet created) reservation, use tempReservationId
    const messageResponse = await createMessage({
      reservation_id: initialData.reservation_id ? initialData.reservation_id : null,
      temp_reservation_id: initialData.reservation_id ? undefined : tempReservationId,
      file_url: file_url,
      content: `File: ${file.name}`
    });

    setUploadingFiles(false);

    if (messageResponse.payload?.success) {
      console.log("Message created successfully with file URL");
      // Optional: clear the file input
      if (e.target) {
        e.target.value = '';
      }
    } else {
      throw new Error('Failed to create message');
    }
  };
  

  // Handle form submission
  const handleSubmit = async () => {
    let errorMessage: string[] = [];
    if (selectedParticipants.length === 0) {
      errorMessage.push('参加者を選択してください');
    }

    if (formData.type_id === 0) {
      errorMessage.push('予約の種類を選択してください');
    }

    if (formData.title.trim() === '') {
      errorMessage.push('タイトルを入力してください');
    }

    if (formData.start_date === '') {
      errorMessage.push('開始日を入力してください');
    }

    if (formData.start_time === '') {
      errorMessage.push('開始時刻を入力してください');
    }

    if (formData.end_date === '' && reservationType === 'multiday') {
      errorMessage.push('終了日を入力してください');
    }

    if (formData.end_time === '') {
      errorMessage.push('終了時刻を入力してください');
    }

    if (!!formData.end_date && formData.start_date > formData.end_date) {
      errorMessage.push('終了日は開始日より後の日付を選択してください');
    }

    if (formData.start_date === formData.end_date && formData.start_time >= formData.end_time) {
      errorMessage.push('終了時刻は開始時刻より後の時間を選択してください');
    }

    if (reservationType === 'recurring' && formData.recurring_frequency === '') {
      errorMessage.push('繰り返しの頻度を選択してください');
    }

    if (reservationType === 'recurring' && (formData.recurring_gap || 0) < 1) {
      errorMessage.push('繰り返しの間隔は1以上の値を入力してください');
    }

    if (reservationType === 'recurring' && formData.recurring_frequency === 'weekly' && !formData.days_of_week) {
      errorMessage.push('繰り返しの曜日を選択してください');
    }

    if (errorMessage.length > 0) {
      setNotificationMessage(errorMessage.join(', '));
      setNotificationType('error');
      setShowNotification(true);
      return;
    }

    setReadyToSubmit(true);

    // Prepare participants array for API
    const participants = selectedParticipants.map(participant => ({
      user_id: participant.user.id || 0,
      required: !!participant.required
    }));
    
    // Get room IDs from selected rooms
    const roomIds = selectedRooms.map(room => room.id);
    
    // Create reservation data
    const reservationData = {
      id: formData.id,
      temp_reservation_id: formData.id ? undefined : tempReservationId,
      title: formData.title,
      description: formData.description,
      room_ids: roomIds,
      type_id: formData.type_id,
      start_date: formData.start_date,
      end_date: reservationType === 'normal' ? formData.start_date : formData.end_date, // "normal" reservations are single-day
      start_time: formData.start_time,
      end_time: formData.end_time,
      days_of_week: formData.days_of_week,
      week_number: formData.week_number,
      recurring_gap: formData.recurring_gap,
      recurring_date: formData.recurring_date,
      day_of_week: formData.day_of_week,
      recurring_month: formData.recurring_month,

      participants,
      recurring_frequency: reservationType === 'recurring' ? formData.recurring_frequency : null,
      is_private: formData.is_private ? true : false,
      color: formData.color,
    };
    
    let messageResponse;
    // Dispatch action to create or update reservation
    if (reservationData.id) {
      messageResponse = await dispatch(updateReservation({...reservationData, id: reservationData.id}));
    } else {
      messageResponse = await dispatch(createReservation({...reservationData}));
    }
    console.log("messageResponse", messageResponse, ", did it go well? ", messageResponse.payload?.success ? "yes" : "no");
    if(messageResponse.payload?.success) {
      setNotificationMessage('予約が正常に作成されました');
      setNotificationType('success');
      setShowNotification(true);
      refreshReservations();
    }

    if (!messageResponse.payload?.success) {
      const errorMessage = Object.values(messageResponse.payload?.message || [messageResponse.payload?.data] || {})
      .flatMap(msgArray => Array.isArray(msgArray) ? msgArray : [msgArray])
      .join(', ') || '予約の作成に失敗しました';
      console.error("Error creating reservation:", errorMessage);
      setNotificationMessage(errorMessage);
      setNotificationType('error');
      setShowNotification(true);
      return;
    }
  };

  const handleDelete = async () => {
    if (formData.id) {
      // Delete the reservation
      const messageResponse = await dispatch(deleteReservation(formData.id));
      console.log("messageResponse", messageResponse, ", did it go well? ", messageResponse.payload?.success ? "yes" : "no");

      if (!messageResponse.payload?.success) {
        const errorMessage = Object.values(messageResponse.payload?.message || [messageResponse.payload?.data] || {})
          .flatMap(msgArray => Array.isArray(msgArray) ? msgArray : [msgArray])
          .join(', ') || '予約の削除に失敗しました';
        console.error("Error deleting reservation:", errorMessage);
        setNotificationMessage(errorMessage);
        setNotificationType('error');
        setShowNotification(true);
        return;
      }

      // Show success message
      setNotificationMessage('予約が正常に削除されました');
      setNotificationType('success');
      setShowNotification(true);
    }
    if (isModal) {
      setTimeout(() => {
        onClose && onClose();
      }, 1500);
    }
  };
  
  // Reset form to initial state
  const resetForm = () => {
    setTempReservationId(Math.floor(Math.random() * 2_000_000) + 10_000);
    dispatch(fetchMessages());
    setTimeout(() => {
      if (!isModal) {
        history.goBack();
      }
    }, 500);
  };
  
  // Handle notification close
  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const recurringFrequencyOptions = [
    { value: '', label: '選択' },
    { value: 'daily', label: '毎日' },
    { value: 'weekly', label: '毎週' },
    { value: 'monthly', label: '毎月' },
    { value: 'yearly', label: '毎年' }
  ];

  const recurringExecutionDateNthDayOfWeekOptions = [
    { value: 1, label: '第一' },
    { value: 2, label: '第二' },
    { value: 3, label: '第三' },
    { value: 4, label: '第四' },
    { value: -1, label: '最終' }
  ];

  const recurringExecutionDateDaysOfWeekOptions = [
    { value: 1, label: '月曜日' },
    { value: 2, label: '火曜日' },
    { value: 3, label: '水曜日' },
    { value: 4, label: '木曜日' },
    { value: 5, label: '金曜日' },
    { value: 6, label: '土曜日' },
    { value: 7, label: '日曜日' }
  ];

  const monthsOptions = [
    { value: 1, label: '1月' },
    { value: 2, label: '2月' },
    { value: 3, label: '3月' },
    { value: 4, label: '4月' },
    { value: 5, label: '5月' },
    { value: 6, label: '6月' },
    { value: 7, label: '7月' },
    { value: 8, label: '8月' },
    { value: 9, label: '9月' },
    { value: 10, label: '10月' },
    { value: 11, label: '11月' },
    { value: 12, label: '12月' },
  ];



  const selectedRecurringOption = recurringFrequencyOptions.find(opt => opt.value === formData.recurring_frequency);

  // Floor + 1 means the start and end dates are inclusive
  // TODO weekly needs to be adjusted (increased) based on selected days of week
  const totalOccurences =
    formData.recurring_frequency === 'daily' ? (1 + Math.floor(moment(formData.end_date).diff(moment(formData.start_date), 'days') / (formData.recurring_gap || 1))) :
    formData.recurring_frequency === 'weekly' ? (1 + Math.floor(moment(formData.end_date).diff(moment(formData.start_date), 'weeks') / (formData.recurring_gap || 1)) * (formData.days_of_week?.length || 1)) :
    formData.recurring_frequency === 'monthly' ? (1 + Math.floor(moment(formData.end_date).diff(moment(formData.start_date), 'months') / (formData.recurring_gap || 1))) :
    formData.recurring_frequency === 'yearly' ? (1 + Math.floor(moment(formData.end_date).diff(moment(formData.start_date), 'years'))) : 0;

  const recurringDescription: string = !selectedRecurringOption?.value ? "繰り返しを選択してください。" :
    selectedRecurringOption.value === 'daily' ? (formData.recurring_gap === 1 ? "毎日繰り返し" : `毎${formData.recurring_gap}日繰り返し`) :
    selectedRecurringOption.value === 'weekly' ? (formData.recurring_gap === 1 ? "毎週繰り返し" : `毎${formData.recurring_gap}週間繰り返し`) :
    selectedRecurringOption.value === 'monthly' ? (formData.recurring_gap === 1 ? "毎月繰り返し" : `毎${formData.recurring_gap}ヶ月繰り返し`) :
    selectedRecurringOption.value === 'yearly' ? "毎年繰り返し" : "不明な繰り返しパターン。";

  const fullRecurringDescription: string = !selectedRecurringOption?.value ? "繰り返しを選択してください。" : `概要: ${recurringDescription}, ${formData.start_date}から${formData.end_date ? `${formData.end_date}まで - (合計${totalOccurences}回実行)` : ""}`;
  // Render date and time inputs based on reservation type
  const renderDateTimeInputs = () => {

    if (reservationType === 'normal' || reservationType === 'recurring') {
      // Normal or recurring single-day reservation
      return (
        <>
        <Grid container spacing={2}>
            <Grid item={true} md={3} sm={4} xs={12}>
              <StyledLabel>
                日付
              </StyledLabel>
              <div style={{display:"flex", alignItems:"center"}}>
              <StyledDateTimeInput
                id="start_date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleFormChange}
                style={{flex: 1, height:'40px', color:'black'}}
              />
              </div>
            </Grid>
            <Grid item={true} md={4} sm={8} xs={12}>
              <StyledLabel>
                時刻
              </StyledLabel>
              <div style={{display:"flex", alignItems:"center"}}>
                <StyledDateTimeInput
                  id="start_time"
                  name="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={handleFormChange}
                  style={{flex: 1, height:'40px', color:'black'}}
                />
                <span style={{ margin:'0 1rem', fontWeight:'bold', fontSize:'14px'}}>〜</span>
                <StyledDateTimeInput
                  id="end_time"
                  name="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={handleFormChange}
                  style={{flex: 1, height:'40px', color:'black'}}
                />
              </div>
            </Grid>
          </Grid>

          <StyledDivider/>
          
          {/* Recurring Frequency (common to all recurring types, keep default to 毎日, don't use option that says 選択) */}
          {reservationType === 'recurring' && (
          <>
          <Grid container sm={8} xs={12} spacing={1}>
            
            <Grid item={true} xs={8} sm={8}>
              <StyledLabel>
                繰り返し
              </StyledLabel>
                <AppCustomeSelect
                  name="recurring_frequency"
                  value={selectedRecurringOption}
                  onChange={(values) => {
                    const endDate = calculateEndDate(formData.start_date, values.value, formData.recurring_gap || 1, formData.recurring_occurences || 1, formData.week_number, formData.day_of_week, formData.recurring_month, formData.days_of_week);

                    const updatedFormData: Partial<Reservation> = {
                      recurring_frequency: values.value,
                      end_date: endDate,
                    };

                    if (values.value === 'yearly' || values.value === 'monthly') {
                      const dayOfMonth = moment(formData.start_date).date(); // Gets day of month (1-31)
                      const monthOfYear = moment(formData.start_date).month() + 1; // Gets month (1-12, moment uses 0-11)

                      updatedFormData.recurring_date = dayOfMonth;
                      updatedFormData.recurring_month = monthOfYear;
                      // Clear week-based options when using date-based
                      updatedFormData.week_number = undefined;
                      updatedFormData.day_of_week = undefined;
                    }

                    setFormData((prev) => ({
                      ...prev,
                      ...updatedFormData,
                    }));
                  }}
                  options={recurringFrequencyOptions}
                  isSearchable={false}
                />
            </Grid>

            {/* DAILY recurring INTERVAL */}
            {formData.recurring_frequency === 'daily' && (
              <Grid item={true} xs={4} sm={3}>
                <StyledLabel>
                繰り返す間隔
                </StyledLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AppInput
                    isNumber
                    value={formData.recurring_gap || 1}
                    min={1}
                    step={1}
                    name="recurring_gap"
                    onChange={(event) => {
                      const endDate = calculateEndDate(formData.start_date, "daily", event.target.value || 1, formData.recurring_occurences || 1, formData.week_number, formData.day_of_week, formData.recurring_month, formData.days_of_week);
                      setFormData((prev) => ({
                        ...prev,
                        recurring_gap: parseInt(event.target.value, 10) || 1,
                        end_date: endDate,
                      }));
                    }}
                  />
                  <Typography sx={{ ml: 0.5 }}>日</Typography>
                  </Box>
              </Grid>
            )}

            {/* WEEKLY recurring INTERVAL */}
            {formData.recurring_frequency === 'weekly' && (
              <Grid item={true} xs={4} sm={3}>
                <StyledLabel>
                繰り返す間隔
                </StyledLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AppInput
                    isNumber
                    value={formData.recurring_gap || 1}
                    min={1}
                    step={1}
                    name="recurring_gap"
                    onChange={(event) => {
                      const endDate = calculateEndDate(formData.start_date, "weekly", event.target.value || 1, formData.recurring_occurences || 1, formData.week_number, formData.day_of_week, formData.recurring_month, formData.days_of_week);
                      setFormData((prev) => ({
                        ...prev,
                        recurring_gap: parseInt(event.target.value, 10) || 1,
                        end_date: endDate,
                      }));
                    }}
                  />
                  <Typography sx={{ ml: 0.5,whiteSpace: 'nowrap' }}>週間</Typography>
                  </Box>
              </Grid>
             )}

             {/* WEEKLY recurring DAYS */}
            {formData.recurring_frequency === 'weekly' && (
              <Grid item={true} xs={12} sm={8} style={{marginTop:'0.5rem'}}>
                <StyledLabel>
                曜日
                </StyledLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ToggleButtonGroup
                    sx={{
                      width: '100%',
                      display: 'flex',
                      '& .MuiToggleButtonGroup-grouped:first-of-type': {
                        borderTopLeftRadius: '10px',
                        borderBottomLeftRadius: '10px',
                      },
                      '& .MuiToggleButtonGroup-grouped:last-of-type': {
                        borderTopRightRadius: '10px',
                        borderBottomRightRadius: '10px',
                      },
                    }}
                    onChange={(event, value) => {
                      console.log("value", value);
                      console.log("event", event);
                      if (value !== null) {
                        setFormData((prev) => ({
                          ...prev,
                          days_of_week: value,
                        }));
                      }
                    }}
                    value={formData.days_of_week}
                  >
                    <StyledToggleButton value={1}>
                      月
                    </StyledToggleButton>
                    <StyledToggleButton value={2}>
                      火
                    </StyledToggleButton>
                    <StyledToggleButton value={3}>
                      水
                    </StyledToggleButton>
                    <StyledToggleButton value={4}>
                      木
                    </StyledToggleButton>
                    <StyledToggleButton value={5}>
                      金
                    </StyledToggleButton>
                    <StyledToggleButton value={6}>
                      土
                    </StyledToggleButton>
                    <StyledToggleButton value={7}>
                      日
                    </StyledToggleButton>
                  </ToggleButtonGroup>    
                </Box>
              </Grid>
             )}

              {/* MONTHLY recurring INTERVAL */}
              {formData.recurring_frequency === 'monthly' && (
                <Grid item={true} xs={4} sm={3}>
                  <StyledLabel>
                  繰り返す間隔
                  </StyledLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AppInput
                      isNumber
                      value={formData.recurring_gap || 1}
                      min={1}
                      step={1}
                      name="recurring_gap"
                      onChange={(event) => {
                        const endDate = calculateEndDate(formData.start_date, "monthly", event.target.value || 1, formData.recurring_occurences || 1, formData.week_number, formData.day_of_week, formData.recurring_month, formData.days_of_week);
                        setFormData((prev) => ({
                          ...prev,
                          recurring_gap: parseInt(event.target.value, 10) || 1,
                          end_date: endDate,
                        }));
                      }}
                    />
                    <Typography sx={{ ml: 0.5,whiteSpace: 'nowrap' }}>ヶ月</Typography>
                    </Box>
                </Grid>
              )}

              {/* YEARLY recurring INTERVAL */}
              {formData.recurring_frequency === 'yearly' && (
                <Grid item={true} xs={4} sm={4}>
                  <StyledLabel>
                  月
                  </StyledLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AppCustomeSelect
                      value={monthsOptions.find(option => option.value === formData.recurring_month) || monthsOptions[0]}
                      options={monthsOptions}
                      onChange={(values) => handleSelectChange(values, "recurring_month")}
                      isSearchable={false}
                    />
                    </Box>
                </Grid>
              )}    

              {/* MONTHLY or YEARLY recurring EXECUTION DATE */}
              {(formData.recurring_frequency === 'monthly' || formData.recurring_frequency === 'yearly') && (
                <Grid item={true} xs={12} sm={12}>
                  <StyledDivider/>
                  <StyledLabel>
                  実行日
                  </StyledLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <RadioGroup
                      row
                      name="recurring_execution_date"
                      value={formData.recurring_date ? "specified_by_day" : "specified_by_day_of_week"}
                      onChange={(event) => {
                        if (event.target.value === "specified_by_day") {
                          const endDate = calculateEndDate(formData.start_date, formData.recurring_frequency || "monthly", formData.recurring_gap || 1, formData.recurring_occurences || 1, formData.week_number, formData.day_of_week, formData.recurring_month, formData.days_of_week);
                          setFormData((prev) => ({
                            ...prev,
                            recurring_date: prev.recurring_date || 1,
                            week_number: undefined,
                            day_of_week: undefined,
                          }));
                        } else {
                          const defaultWeekNumber = recurringExecutionDateNthDayOfWeekOptions[0]?.value || 1;
                          const defaultDayOfWeek = recurringExecutionDateDaysOfWeekOptions[0]?.value || 1;
                          const endDate = calculateEndDate(
                            formData.start_date, 
                            formData.recurring_frequency || "monthly", 
                            formData.recurring_gap || 1, 
                            formData.recurring_occurences || 1, 
                            defaultWeekNumber, 
                            defaultDayOfWeek, 
                            formData.recurring_month, 
                            formData.days_of_week
                          );
                          setFormData((prev) => ({
                            ...prev,
                            recurring_date: undefined,
                            week_number: defaultWeekNumber,
                            day_of_week: defaultDayOfWeek,
                            end_date: endDate,
                          }));
                        }
                      }}
                    >
                      <FormControlLabel
                        value="specified_by_day"
                        control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }} />}
                        label="日にちで指定"
                      />
                      <FormControlLabel
                        value="specified_by_day_of_week"
                        control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }} />}
                        label="曜日で指定"
                      />
                    </RadioGroup>

                    {/* Show the appropriate input based on selection */}
                    {formData.recurring_date ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography style={{width: "5rem", whiteSpace: 'nowrap'}}>日にち:{moment(formData.start_date).format("DD")}</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography style={{width: "5rem", whiteSpace: 'nowrap'}}>曜日:</Typography>
                        <AppCustomeSelect
                          options={recurringExecutionDateNthDayOfWeekOptions}
                          value={recurringExecutionDateNthDayOfWeekOptions.find(opt => opt.value === formData.week_number) || recurringExecutionDateNthDayOfWeekOptions[0]}
                          onChange={(values) => {
                            const endDate = calculateEndDate(formData.start_date, formData.recurring_frequency || "monthly", formData.recurring_gap || 1, formData.recurring_occurences || 1, values.value, formData.day_of_week, formData.recurring_month, formData.days_of_week);
                            //console.log("week_number", values.value);
                            setFormData((prev) => ({
                              ...prev,
                              end_date: endDate,
                              week_number: values.value
                            }));
                          }}
                          isSearchable={false}
                        />
                        <AppCustomeSelect
                          options={recurringExecutionDateDaysOfWeekOptions}
                          value={recurringExecutionDateDaysOfWeekOptions.find(opt => opt.value === formData.day_of_week) || recurringExecutionDateDaysOfWeekOptions[0]}
                          onChange={(values) => {
                            //console.log("day_of_week", values.value);
                            const endDate = calculateEndDate(formData.start_date, formData.recurring_frequency || "monthly", formData.recurring_gap || 1, formData.recurring_occurences || 1, formData.week_number, values.value, formData.recurring_month, formData.days_of_week);
                            setFormData((prev) => ({
                              ...prev,
                              end_date: endDate,
                              day_of_week: values.value
                            }));
                          }}
                          isSearchable={false}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              

          </Grid>

          

          <Grid container sm={12} xs={12} spacing={0}>

          <StyledDivider/>
            
            {/* Recurring end date */}
            {(formData.recurring_frequency === 'daily' || formData.recurring_frequency === 'weekly' || formData.recurring_frequency === 'monthly' || formData.recurring_frequency === 'yearly') && (
              <Grid item={true} xs={12} sm={12}>
                <StyledLabel>
                終了日
                </StyledLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>

                  {/* Radio group starts here */}
                  <RadioGroup
                    row
                    name="recurring_end_date_options"
                    value={recurringEndOption}
                    defaultValue="occurrences"
                    onChange={(event) => {
                      setRecurringEndOption(event.target.value as any);
                      console.log("start_date changed", event.target.value, "formData.occurrences: ", formData.recurring_occurences);
                      if (event.target.value === 'forever') {
                        setFormData((prev) => ({
                          ...prev,
                          end_date: "",
                          recurring_occurences: undefined,
                        }));
                      }

                      // If we use occurrences, we need to calculate the end date based on start date and gap
                      if (event.target.value === 'occurrences') {
                        const endDate = calculateEndDate(formData.start_date, formData.recurring_frequency || "monthly", formData.recurring_gap || 1, formData.recurring_occurences || 1, formData.week_number, formData.day_of_week, formData.recurring_month, formData.days_of_week);
                        setFormData((prev) => ({
                          ...prev,
                          end_date: endDate,
                          recurring_occurences: formData.recurring_occurences || 1,
                        }));
                      }

                      // If we use a specific date, we need to calculate the number of occurrences
                      if (event.target.value === 'specific_date') {
                        const occurrences = calculateOccurrences(formData.start_date, formData.end_date, formData.recurring_frequency || "monthly", formData.recurring_gap || 1, formData.week_number, formData.day_of_week, formData.recurring_month, formData.days_of_week);

                        setFormData((prev) => ({
                          ...prev,
                          recurring_occurences: occurrences > 0 ? occurrences : 1,
                        }));
                      }
                    }}
                    sx={{ alignItems: 'flex-start', gap: { xs: 0, sm: 0, md: 0 } }}
                  >

                    {/* Option: "forever" since the date doesn't exist */}
                    <FormControlLabel
                      value="forever"
                      control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }} />}
                      label={<Typography variant="body2">指定なし</Typography>}
                      labelPlacement={radioLabelPlacement}
                    />

                    {/* Option: number of occurrences */}
                    <FormControlLabel
                      value="occurrences"
                      control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }} />}
                      labelPlacement={radioLabelPlacement}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', width:'100px' }}>
                          <AppInput isNumber min={1} step={1} value={formData.recurring_occurences || 1} name="occurrences" onChange={(event) => {
                            setRecurringEndOption('occurrences');
                            const endDate = calculateEndDate(formData.start_date, formData.recurring_frequency || "monthly", formData.recurring_gap || 1, event.target.value || 1, formData.week_number, formData.day_of_week, formData.recurring_month, formData.days_of_week);

                            setFormData((prev) => ({
                              ...prev,
                              end_date: endDate,
                              recurring_occurences: Number(event.target.value) || 1,
                            }));
                          }}/>
                          <Typography sx={{ ml: 0.5 }} variant="body2">回</Typography>
                        </Box>
                      }
                    />

                    {/* Option: specific date */}
                    <FormControlLabel
                      value="specific_date"
                      control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }} />}
                      labelPlacement={radioLabelPlacement}
                      label={
                        <StyledDateTimeInput
                          id="end_date"
                          name="end_date"
                          type="date"
                          value={formData.end_date}
                          onChange={(event) => {
                            setRecurringEndOption('specific_date');
                            handleFormChange(event);
                            const occurrences = calculateOccurrences(formData.start_date, event.target.value, formData.recurring_frequency || "monthly", formData.recurring_gap || 1, formData.week_number, formData.day_of_week, formData.recurring_month);
                            setFormData((prev) => ({
                              ...prev,
                              end_date: event.target.value,
                              recurring_occurences: occurrences > 0 ? occurrences : 1,
                            }));
                          }}
                          style={{height:'40px', color:'black'}}
                        />
                      }
                    />

                  </RadioGroup>
                </Box>
              </Grid>
            )}

          </Grid>

          <StyledDivider/>

         {/* Recap (display) of the recurring settings based on the above options */}
          <Grid container sm={12} xs={12} spacing={0} style={{
              backgroundColor:'floralwhite',
              padding:'10px',
              display:"none",//'flex',
              justifyContent: 'center',
              borderRadius:'10px'
            }}>
            <Typography sx={{ fontSize: '18px !important', fontWeight: 'bold' }}>
              {fullRecurringDescription}
            </Typography>
          </Grid>

          </>
          )}
        </>
      );
    } else if (reservationType === 'multiday') {
      // Multi-day reservation
      return (
        <>
        <Grid container spacing={0} xs={10}>
          <Grid container item spacing={2} sm={5} xs={12}>
            <Grid item={true} sm={8} xs={7}>
              <StyledLabel>
                開始日時
              </StyledLabel>
              <div style={{display:"flex", alignItems:"center"}}>
              <StyledDateTimeInput
                  id="start_date"
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleFormChange}
                  style={{flex:1,height:'40px', color:'black'}}
                />
            </div>
             </Grid>
              <Grid item={true} sm={4} xs={5}>
              <StyledLabel>
                &nbsp;
              </StyledLabel>
              <div style={{display:"flex", alignItems:"center"}}>
                <StyledDateTimeInput
                  id="start_time"
                  name="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={handleFormChange}
                  style={{flex:1,height:'40px', color:'black'}}
                />
                  <Typography
                    component="span"
                    sx={{
                      display: { xs: 'inline', sm: 'none' }, 
                      ml: 1, 
                      fontSize: '14px', 
                      alignSelf: 'center'
                    }}
                  >
                  から
                  </Typography>
                </div>
              </Grid>
            </Grid>

            <Box style={{ margin: '0 2rem', marginTop:'20px', fontWeight: 'bold', fontSize: '14px', alignSelf: 'center'}} 
              sx={{display: { xs: 'none', sm: 'block'} }} component="span">〜</Box>

            <Box
                  sx={{
                    display: { xs: 'block', sm: 'none' }, 
                    width: '100%',              
                    height: (theme) => theme.spacing(2), 
                  }}
                />

            <Grid container item spacing={2} sm={5} xs={12}>
            <Grid item={true} sm={8} xs={7}>
              <StyledLabel>
                終了日時
              </StyledLabel>
              <div style={{display:"flex", alignItems:"center"}}>
              <StyledDateTimeInput
                  id="end_date"
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleFormChange}
                  style={{flex:1, height:'40px', color:'black'}}
                />
              </div>
              </Grid>
              <Grid item={true} sm={4} xs={5}>
              <StyledLabel>
                  &nbsp;
                </StyledLabel>
                <div style={{display:"flex", alignItems:"center"}}>
                <StyledDateTimeInput
                  id="end_time"
                  name="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={handleFormChange}
                  style={{flex:1, height:'40px', color:'black'}}
                />
                <Typography
                    component="span"
                    sx={{
                      display: { xs: 'inline', sm: 'none' }, 
                      ml: 1, 
                      fontSize: '14px', 
                      alignSelf: 'center'
                    }}
                  >
                  まで
                  </Typography>
                </div>
              </Grid>
              </Grid>
            </Grid>
        </>
      );
    } else {
      return null;
    }
  };
  
  const typeOptions = [
    { value: 0, label: '選択' },
    ...(types?.data || [])
      .filter(type => !type.deleted_at)
      .map(type => ({
        value: type.id,
        label: type.text
      }))
  ];
  const selectedTypeOption = typeOptions.find(opt => opt.value === formData.type_id);

  // This "message" is actually a file attached to this reservation if:
  // 1. The message has a file_url
  // AND
  // 2. The message is either associated with this reservation (reservation_id) OR the temp_reservation_id matches the current tempReservationId
  const attachedFiles = messages.data.filter(message => message.file_url && ((message.reservation_id && message.reservation_id === formData.id) || message.temp_reservation_id === tempReservationId));

  // Render the form content based on reservation type
  const renderFormContent = () => {
    return (
      <Grid container>
        {/* Date and Time */}
        {renderDateTimeInputs()}
        {/* Reservation Type and Title */}
        <Grid container spacing={2} style={{marginTop:'0.5rem'}}>
          <Grid item={true} xs={11} sm={4} md={3}>
          <input type='button' onClick={() => {console.log("formData", formData);}} value="デバッグ" style={{display: "none"}}/>
            <StyledLabel>
              予定タイプ
            </StyledLabel>
              <AppCustomeSelect
                name="type_id"
                value={selectedTypeOption}
                onChange={(values) => {changeColorToDefault(values.value);}}
                options={typeOptions}
                isSearchable={false}
                style={{
                  fontSize:"13px"
                }}
              />
          </Grid>
          <Grid item={true} xs={1} sm={1} md={1}>
              <StyledLabel>
               &nbsp;
              </StyledLabel>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                style={{ display: "none", width: '40px', height: '40px', border:'none', cursor:'pointer' }}
              />
              <div
                style={{
                  width: '20px', height: '20px', border:'1px solid #bbb', backgroundColor: formData.color,
                  borderRadius:"50%",marginLeft:"-5px", marginTop:"12px"
                }}
              />
            </Grid>
          <Grid item={true} xs={12} sm={12} md={8}>
            <StyledLabel>
              タイトル
            </StyledLabel>
            <AppInput
              name="title"
              value={formData.title}
              onChange={handleFormChange}
              placeholder="タイトル"
            />
          </Grid>
        </Grid>
        
        {/* Description */}

          <Grid container spacing={2} style={{marginTop:'0.5rem'}}> 
          {/* Description */}
          <Grid item={true} xs={12} md={7}> 
            <StyledLabel>
              説明
            </StyledLabel>
            <AppTextArea
              name="description"
              value={formData.description}
              onChangeEvent={handleFormChange}
              rows={isMobileLayout ? 3 : 9} 
            />
          </Grid>

        {/* File uploads */}
        <Grid item={true} xs={12} md={5}>
          <StyledLabel>
            添付ファイル
          </StyledLabel>
          <div id="reservation-attachments" style={{ marginTop:"1rem", fontSize:"14px"}}>
            {attachedFiles.length > 0 ? 
              attachedFiles.map((message, index) => {
                const fileName = message.content ? message.content.replace('File: ', '') : 'File';
                const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName);
                
                return (
                  <div key={index} style={{display:"flex", flexDirection:"row", alignItems:"center"}}>
                    <div 
                      id={`attachment-file-${index}`} 
                      className="attachment-file" 
                      style={{
                        display: "flex",
                        width:"100%",
                        borderRadius: "6px", 
                        border: "1px solid wheat",
                        padding: "5px 10px",
                        marginBottom: "1rem",
                        color: "inherit",
                        background: "ivory",
                        alignItems: "center"
                      }}
                    >
                      {isImage ? 
                        <IconFileImage style={{ marginRight: '8px', width: '20px', height: '20px' }} />
                        : 
                        <IconFileFile style={{ marginRight: '8px', width: '20px', height: '20px' }} />
                      }
                      <div id="file-info" style={{ fontSize: "12px"}}>
                        <div id="file-description">
                          {(message.content || '').replace(/^File:\s*/i, '')}
                        </div>
                      </div>
                    </div>
                    <div 
                      onClick={() => deleteMessage(message.id)} 
                      style={{
                        display: 'flex',        
                        alignItems: 'center',   
                        justifyContent: 'center',
                        width: '20px',        
                        height: '20px',  
                        minWidth: '20px',        
                        minHeight: '20px',      
                        padding: '0',          
                        fontSize: '12px',  
                        fontWeight: "bold",     
                        borderRadius: '50%',    
                        backgroundColor: '#888',
                        color: 'white',
                        cursor: 'pointer',
                        marginLeft: '8px',
                        boxSizing: 'border-box',
                      }}
                    >
                      ×
                    </div>
                  </div>
                );
              }) : uploadingFiles ? (
              <p>ファイルをアップロード中...</p>
            ) : (
              <p style={{color:"#aaa"}}>ファイルは添付されていません。</p>
            )}
          </div>
          <label htmlFor="file-input" style={{ marginTop:"1rem",fontSize:"12px",cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', border: '1px dashed #ccc', padding: '1rem', borderRadius: '1rem' }}>
            <IconPlus style={{ marginRight: '8px', width: '20px', height: '20px' }}/>ファイルを追加
          </label>
          <input
            id="file-input"
            type="file"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
         </Grid>
        </Grid>

        <StyledDivider/>
        
        <ParticipantsSelector
          currentUserId={currentUser?.id || 0}
          reservationId={formData.id}
          originalReservation={originalReservation}
          users={users}
          groups={groups}
          groupsUsers={preloadData.groupsUsers}
          selectedUsers={selectedParticipants}
          setSelectedUsers={setSelectedParticipants}
          showCheckbox={true}
          leftListHeight={isMobileLayout ? "350px" : "400px"} 
          rightListHeight={isMobileLayout ? "252px" : "302px"} 
        />
        
        {/* Facility / Rooms */}
        <Grid item={true} xs={12} style={{marginTop:'2rem'}}>
          <StyledLabel>
            施設
          </StyledLabel>
          <Grid container spacing={1}>
          <Grid item={true} xs={6} sm={6}>
              <ParticipantList>
                {selectedRooms.map((room) => (
                  <ListItem
                    key={room.id}
                    onClick={() => {
                      if (selectedCurrentRooms.includes(room.id)) {
                        setSelectedCurrentRooms(selectedCurrentRooms.filter(id => id !== room.id));
                      } else {
                        setSelectedCurrentRooms([...selectedCurrentRooms, room.id]);
                      }
                    }}
                    divider
                    disableGutters
                    sx={{
                      pl:1,
                      bgcolor: unavailableRoomIds.includes(room.id) 
                        ? 'rgba(255, 0, 0, 0.1)' 
                        : selectedCurrentRooms.includes(room.id) 
                          ? '#FFCEAF' 
                          : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={room.name}
                      secondary={`定員: ${room.capacity}人${unavailableRoomIds.includes(room.id) ? ' (予約済み)' : ''}`}
                      sx={{
                        
                        '& .MuiListItemText-primary': {
                          color: unavailableRoomIds.includes(room.id) 
                          ? 'rgba(255,0,0,0.5)'
                          : 'inherit',
                          fontWeight: 'bold',
                          fontSize:'13px !important',
                        },
                        '& .MuiListItemText-secondary': {
                          fontSize:'13px !important',
                          color: unavailableRoomIds.includes(room.id) 
                          ? 'rgba(255,0,0,0.5)'
                          : 'inherit'
                        },
                    }}
                    />
                  </ListItem>
                ))}
              </ParticipantList>
              <PassChangeButton
                style={{width:'100%', marginTop:'1rem'}}
                onClick={handleRemoveRooms}
                  disabled={selectedCurrentRooms.length === 0}
              >施設を削除 →</PassChangeButton>
              <div style={{fontSize:'12px', marginTop:'5px'}}>
             
              </div>
            </Grid>
            
            
            <Grid item={true} xs={6} sm={6}>
              <ParticipantList>
                {availableRooms.map((room) => (
                  <ListItem
                    key={room.id}
                    onClick={() => {
                      // Only allow selecting available rooms
                      if (!unavailableRoomIds.includes(room.id)) {
                        if (selectedAvailableRooms.includes(room.id)) {
                          setSelectedAvailableRooms(selectedAvailableRooms.filter(id => id !== room.id));
                        } else {
                          setSelectedAvailableRooms([...selectedAvailableRooms, room.id]);
                        }
                      }
                    }}
                    divider
                    disableGutters
                    sx={{
                      pl:1,
                      bgcolor: unavailableRoomIds.includes(room.id) 
                        ? 'transparent'
                        : selectedAvailableRooms.includes(room.id) 
                          ? '#FFCEAF' 
                          : 'transparent',
                      cursor: unavailableRoomIds.includes(room.id) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <ListItemText
                      primary={room.name}
                      secondary={`定員: ${room.capacity}人${unavailableRoomIds.includes(room.id) ? ' (予約済み)' : ''}`}
                      sx={{
                        
                          '& .MuiListItemText-primary': {
                            color: unavailableRoomIds.includes(room.id) 
                            ? 'rgba(255,0,0,0.5)'
                            : 'inherit',
                            fontWeight: 'bold',
                            fontSize:'13px !important',
                          },
                          '& .MuiListItemText-secondary': {
                            fontSize:'13px !important',
                            color: unavailableRoomIds.includes(room.id) 
                            ? 'rgba(255,0,0,0.5)'
                            : 'inherit'
                          },
                      }}
                    />
                  </ListItem>
                ))}
              </ParticipantList>
              <PassChangeButton
                style={{width:'100%', marginTop:'1rem'}}
                onClick={handleAddRooms}
                  disabled={selectedAvailableRooms.length === 0}
              >← 施設を追加</PassChangeButton>
            </Grid>
          </Grid>
        </Grid>
        
        {/* Public/Private */}
        <Grid item={true} xs={12} sm={5.5} md={5.5} sx={{mt:2, mb:2}}>
        <ToggleButtonGroup
          value={formData.is_private ? 'private' : 'public'}
          exclusive 
          onChange={handlePublicChange}
          sx={{ width: '100%',
              display: 'flex',
              '& .MuiToggleButtonGroup-grouped:first-of-type': {
              borderTopLeftRadius: '30px',
              borderBottomLeftRadius: '30px',
            },
            '& .MuiToggleButtonGroup-grouped:last-of-type': {
              borderTopRightRadius: '30px',
              borderBottomRightRadius: '30px',
            },
            }}
          >
            <StyledToggleButton value="public">
              公開
            </StyledToggleButton>
            <StyledToggleButton value="private">
              非公開
            </StyledToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>
    );
  };
  
  const tabValue = reservationType === 'normal' ? 0 : reservationType === 'multiday' ? 1 : 2;

  return (
    <div>
       <div>
       <FormTitleHeader>
       <span>予定の登録 {formData.id ? `(編集 #${formData.id})` : ''}</span>
      <IconButton
        aria-label="close"
        onClick={isModal ? onClose : resetForm}
        sx={{ color: (theme) => theme.palette.grey[700],
        fontSize:'24px',
        padding: 0,
        paddingLeft:'4px',
        paddingRight:'4px',
        '&:hover': {
        backgroundColor: 'transparent',
    }}}>
        ×
    </IconButton>
    </FormTitleHeader>
        <div style={{padding:'0'}}>
        {/* Reservation Type Tabs */}
        <TabContainer>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            aria-label="reservation type tabs"
            variant="standard"
            sx={{
              paddingLeft:'10px',
              '& .MuiTabs-indicator': {
                backgroundColor: '#ff6a0c',
              },
              '& .MuiTab-root.Mui-selected': {
                color: 'black',
              },
              '& .MuiTab-root.Mui-disabled': {
                opacity: 0.4,
              },
              ...(!!formData.id && {
                '.MuiTab-root': {
                  cursor: 'default',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              }),
            }}
          >
            <Tab label="通常" disabled={!!formData.id && reservationType !== "normal"}/>
            <Tab label="期間指定" disabled={!!formData.id && reservationType !== "multiday"}/>
            <Tab label="繰り返し" disabled={!!formData.id && reservationType !== "recurring"}/>
          </Tabs>
        </TabContainer>
        
        {/* Normal Reservation Form */}
        <TabPanel value={tabValue} index={0}>
            {renderFormContent()}
        </TabPanel>
        
        {/* Multiday Tab Panel */}
        <TabPanel value={tabValue} index={1}>
            {renderFormContent()}
        </TabPanel>
        
        {/* Recurring Tab Panel */}
        <TabPanel value={tabValue} index={2}>
            {renderFormContent()}
        </TabPanel>
        
        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: '1rem',
            padding: '8px',
            position: "sticky",
            bottom: 0,
            borderTop: "1px solid #bbb",
            background: "white",
            width: '100%',
            '& > *': { 
              flex: 1, 
            },
          }}
        >
          <PassChangeButton
            style={{display: formData.id ? undefined : 'none'}}
            onClick={handleDelete}
            disabled={loading}
            danger={true}
          >
            予約を削除
          </PassChangeButton>
          <PassChangeButton
            onClick={isModal ? onClose : resetForm}
            disabled={loading}
            danger={true}
          >
            キャンセル
          </PassChangeButton>
          <PassChangeButton
            onClick={handleSubmit}
            disabled={loading || (reservationType === 'recurring' && !formData.recurring_frequency)}
          >
            {loading ? <CircularProgress size={12} /> : '登録する'}
          </PassChangeButton>
          </Box>



        </div>
      </div>
      
      {/* Success/Error Notification */}
      <Snackbar
        open={showNotification}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notificationType}
          sx={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            width: '100%'
          }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ReservationForm;

// Styled components

const FormTitleHeader = scStyled.div`
  background-color: ${(p) => p.theme.colors.lightgray09};
  padding:0.5rem;
  padding-left:1rem;
  font-size:14px;
  font-weight:bold;
  text-align:left;
  display: flex;    
  justify-content: space-between; 
  align-items: center;
  position: absolute;
    top: 0;
    z-index: 10;
    border-bottom:1px solid #bbb;
    width:100%;
    padding-right:1rem;
`;

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  border: '1px solid #464646',
  flexGrow: 1,
  fontWeight: 'bold',
  fontSize: '12px',
  padding: '8px',
  '&.Mui-selected': {
    backgroundColor: '#464646',
    color: 'white',
    '&:hover': {
      backgroundColor: '#5a5a5a',
    },
  },
}));

const TabContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTab-root': {
    fontSize: '14px',
    fontWeight: 'bold'
  },
}));

const ParticipantList = scStyled(List)`
  height: 300px;
  overflow-y: auto;
  border: 1px solid lightgray;
  border-radius: 10px;
  background-color:${(p) => p.theme.colors.white02};
  @media (min-width: 768px) {
    height: 250px; 
  }
  padding-top:0 !important;
  &::-webkit-scrollbar {
    width: 11px;
  }
    &::-webkit-scrollbar-track {
    background: rgba(0,0,0,0.04);
  }
`;

const ParticipantActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(1),
}));

const StyledDateTimeInput = scStyled.input`
  border-radius: 50px;
  padding:0.5rem 1rem;
  border: 1px solid lightgray;
  background-color:${(p) => p.theme.colors.white02};
  font-size: 14px;
  font-family: ${(p) => p.theme.fontFamily.NotoSansJP_400};
  width:100%;
  box-sizing: border-box;
  min-width: 0;
`;

const StyledLabel = scStyled.div`
  font-size: 14px;
  margin-bottom:3px;
`;

const StyledDivider = scStyled.div`
  border-top:1px solid ${(p) => p.theme.colors.lightgray09};
  width: 100%; 
  margin:1rem 0;
  padding:0;
`;

const calculateEndDate = (
  startDate: string, 
  frequency: string, 
  gap: number, 
  occurrences: number, 
  weekNumber?: number, 
  dayOfWeek?: number, 
  month?: number,
  daysOfWeek?: number[],
): string => {
  if (!startDate || !frequency || !gap || !occurrences) return '';
  
  const startMoment = moment(startDate);
  let endMoment;
  
  switch (frequency) {
    case 'daily':
      endMoment = startMoment.clone().add((occurrences - 1) * gap, 'days');
      break;
      
    case 'weekly':
      endMoment = calculateWeeklyEndDate(startMoment, gap, occurrences, daysOfWeek);
      break;
      
    case 'monthly':
      endMoment = calculateMonthlyEndDate(startMoment, gap, occurrences, weekNumber, dayOfWeek);
      break;
      
    case 'yearly':
      endMoment = calculateYearlyEndDate(startMoment, 1, occurrences, weekNumber, dayOfWeek, month); // yearly events can't have gaps
      break;
      
    default:
      return '';
  }
  
  return endMoment ? endMoment.format('YYYY-MM-DD') : '';
};

const calculateWeeklyEndDate = (
  startMoment: moment.Moment, 
  gap: number, 
  occurrences: number, 
  daysOfWeek?: number[]
): moment.Moment => {
  if (!daysOfWeek || daysOfWeek.length === 0) {
    // Simple weekly - same day each week
    return startMoment.clone().add((occurrences - 1) * gap, 'weeks');
  }

  // Complex weekly - multiple days of week
  const occurrenceDates: moment.Moment[] = [];
  let currentWeek = startMoment.clone();
  const startDay = startMoment.isoWeekday();

  while (occurrenceDates.length < (occurrences*daysOfWeek.length)) {
    // Check each day of the week in this week
    for (const dayOfWeek of [...daysOfWeek].sort()) {
      const targetDate = currentWeek.clone().add(dayOfWeek - startDay, 'days');
      
      // Only include if it's on or after the start date
      if (targetDate.isSameOrAfter(startMoment, 'day')) {
        occurrenceDates.push(targetDate);
        
        // Stop if we've found enough occurrences
        if (occurrenceDates.length >= (occurrences*daysOfWeek.length)) {
          break;
        }
      }
    }

    // Move to next week according to gap
    currentWeek.add(gap, 'weeks');
  }  
  // console.log("occurrenceDates", occurrenceDates.map(date => date.format('YYYY-MM-DD')));
  return occurrenceDates[(occurrences*daysOfWeek.length) - 1];
};

const calculateMonthlyEndDate = (
  startMoment: moment.Moment, 
  gap: number, 
  occurrences: number, 
  weekNumber?: number, 
  dayOfWeek?: number
): moment.Moment => {
  if (weekNumber === undefined || dayOfWeek === undefined) {
    // Simple monthly - same day each month
    return startMoment.clone().add((occurrences - 1) * gap, 'months');
  }
  
  // Complex monthly - specific week and day of month
  const occurrenceDates: moment.Moment[] = [];
  let currentMonth = startMoment.clone().startOf('month');
  
  while (occurrenceDates.length < occurrences) {
    const targetDate = findDateInMonth(currentMonth, weekNumber, dayOfWeek);
    
    // Only include if it's on or after the start date
    if (targetDate.isSameOrAfter(startMoment, 'day')) {
      occurrenceDates.push(targetDate);
    }
    
    // Move to next month according to gap
    currentMonth.add(gap, 'months');
  }
  
  return occurrenceDates[occurrences - 1];
};

const calculateYearlyEndDate = (
  startMoment: moment.Moment, 
  gap: number, 
  occurrences: number, 
  weekNumber?: number, 
  dayOfWeek?: number, 
  month?: number
): moment.Moment => {
  if (month === undefined) {
    // Simple yearly - same date each year
    return startMoment.clone().add((occurrences - 1) * gap, 'years');
  }
  
  if (weekNumber === undefined || dayOfWeek === undefined) {
    // Yearly on specific month and day
    return startMoment.clone().month(month - 1).add((occurrences - 1) * gap, 'years');
  }
  
  // Complex yearly - specific week, day, and month
  const occurrenceDates: moment.Moment[] = [];
  let currentYear = startMoment.clone().startOf('year');
  
  while (occurrenceDates.length < occurrences) {
    const targetMonth = currentYear.clone().month(month - 1).startOf('month');
    const targetDate = findDateInMonth(targetMonth, weekNumber, dayOfWeek);
    
    // Only include if it's on or after the start date
    if (targetDate.isSameOrAfter(startMoment, 'day')) {
      occurrenceDates.push(targetDate);
    }
    
    // Move to next year according to gap
    currentYear.add(gap, 'years');
  }
  
  return occurrenceDates[occurrences - 1];
};

const findDateInMonth = (
  monthStart: moment.Moment, 
  weekNumber: number, 
  dayOfWeek: number
): moment.Moment => {
  const month = monthStart.clone();
  
  if (weekNumber === -1) {
    // Last occurrence of dayOfWeek in the month
    const lastDayOfMonth = month.clone().endOf('month');
    const lastWeekday = lastDayOfMonth.clone();
    
    // Move backwards to find the last occurrence of the target day
    while (lastWeekday.isoWeekday() !== dayOfWeek) {
      lastWeekday.subtract(1, 'day');
    }
    
    return lastWeekday;
  } else {
    // Nth occurrence of dayOfWeek in the month (1st, 2nd, 3rd, 4th)
    const firstDayOfMonth = month.clone().startOf('month');
    let targetDate = firstDayOfMonth.clone();
    
    // Find the first occurrence of the target day
    while (targetDate.isoWeekday() !== dayOfWeek) {
      targetDate.add(1, 'day');
    }
    
    // Add weeks to get to the Nth occurrence
    targetDate.add((weekNumber - 1) * 7, 'days');
    
    // Check if we're still in the same month
    if (targetDate.month() !== month.month()) {
      throw new Error(`Week ${weekNumber} of day ${dayOfWeek} does not exist in ${month.format('YYYY-MM')}`);
    }
    
    return targetDate;
  }
};

// console.log('Daily example:', calculateEndDate('2024-01-01', 'daily', 2, 5)); // Every 2 days, 5 occurrences
// console.log('Weekly example:', calculateEndDate('2024-01-03', 'weekly', 3, 3, undefined, undefined, undefined, [1,5])); // Every week, 3 occurrences
// console.log('Monthly example (2nd Tuesday):', calculateEndDate('2024-01-01', 'monthly', 1, 3, 2, 2)); // 2nd Tuesday of each month, 3 occurrences
// console.log('Yearly example (last Friday of March):', calculateEndDate('2024-01-01', 'yearly', 1, 2, -1, 5, 3)); // Last Friday of March each year, 2 occurrences

const calculateOccurrences = (
  startDate: string,
  endDate: string,
  frequency: string,
  gap: number,
  weekNumber?: number,
  dayOfWeek?: number,
  month?: number,
  daysOfWeek?: number[]
): number => {
  if (!startDate || !endDate || !frequency || !gap) return 0;
  
  const startMoment = moment(startDate);
  const endMoment = moment(endDate);
  
  if (endMoment.isBefore(startMoment)) return 0;
  
  switch (frequency) {
    case 'daily':
      return calculateDailyOccurrences(startMoment, endMoment, gap);
      
    case 'weekly':
      return calculateWeeklyOccurrences(startMoment, endMoment, gap, daysOfWeek);
      
    case 'monthly':
      return calculateMonthlyOccurrences(startMoment, endMoment, gap, weekNumber, dayOfWeek);
      
    case 'yearly':
      return calculateYearlyOccurrences(startMoment, endMoment, weekNumber, dayOfWeek, month);
      
    default:
      return 0;
  }
};

const calculateDailyOccurrences = (
  startMoment: moment.Moment,
  endMoment: moment.Moment,
  gap: number
): number => {
  const daysDifference = endMoment.diff(startMoment, 'days');
  return Math.floor(daysDifference / gap) + 1;
};

const calculateWeeklyOccurrences = (
  startMoment: moment.Moment,
  endMoment: moment.Moment,
  gap: number,
  daysOfWeek?: number[]
): number => {
  if (!daysOfWeek || daysOfWeek.length === 0) {
    // Simple weekly - same day each week
    const weeksDifference = endMoment.diff(startMoment, 'weeks');
    return Math.floor(weeksDifference / gap) + 1;
  }

  // Complex weekly - multiple days of week
  let count = 0;
  let currentWeek = startMoment.clone();
  const startDay = startMoment.isoWeekday();

  while (currentWeek.isSameOrBefore(endMoment, 'week')) {
    // Check each day of the week in this week
    for (const dayOfWeek of [...daysOfWeek].sort()) {
      const targetDate = currentWeek.clone().add(dayOfWeek - startDay, 'days');
      
      // Check if this occurrence falls within our date range
      if (targetDate.isSameOrAfter(startMoment, 'day') && 
          targetDate.isSameOrBefore(endMoment, 'day')) {
        count++;
      }
    }

    // Move to next week according to gap
    currentWeek.add(gap, 'weeks');
  }

  count = Math.round(count / daysOfWeek.length); // 8 events, 2 per week, the whole thing is repeated 4 times, that's the number we want
  return count;
};

const calculateMonthlyOccurrences = (
  startMoment: moment.Moment,
  endMoment: moment.Moment,
  gap: number,
  weekNumber?: number,
  dayOfWeek?: number
): number => {
  if (weekNumber === undefined || dayOfWeek === undefined) {
    // Simple monthly - same day each month
    const monthsDifference = endMoment.diff(startMoment, 'months');
    return Math.floor(monthsDifference / gap) + 1;
  }
  
  // Complex monthly - specific week and day of month
  let count = 0;
  let currentMonth = startMoment.clone().startOf('month');
  
  while (currentMonth.isSameOrBefore(endMoment, 'month')) {
    try {
      const targetDate = findDateInMonth(currentMonth, weekNumber, dayOfWeek);
      
      // Check if this occurrence falls within our date range
      if (targetDate.isSameOrAfter(startMoment, 'day') && 
          targetDate.isSameOrBefore(endMoment, 'day')) {
        count++;
      }
    } catch (error) {
      // Skip months where the target week/day doesn't exist
    }
    
    // Move to next month according to gap
    currentMonth.add(gap, 'months');
  }
  
  return count;
};

const calculateYearlyOccurrences = (
  startMoment: moment.Moment,
  endMoment: moment.Moment,
  weekNumber?: number,
  dayOfWeek?: number,
  month?: number
): number => {
  if (month === undefined) {
    // Simple yearly - same date each year
    const yearsDifference = endMoment.diff(startMoment, 'years');
    return yearsDifference + 1;
  }
  
  if (weekNumber === undefined || dayOfWeek === undefined) {
    // Yearly on specific month and day
    const yearsDifference = endMoment.diff(startMoment, 'years');
    return yearsDifference + 1;
  }
  
  // Complex yearly - specific week, day, and month
  let count = 0;
  let currentYear = startMoment.clone().startOf('year');
  
  while (currentYear.isSameOrBefore(endMoment, 'year')) {
    try {
      const targetMonth = currentYear.clone().month(month - 1).startOf('month');
      const targetDate = findDateInMonth(targetMonth, weekNumber, dayOfWeek);
      
      // Check if this occurrence falls within our date range
      if (targetDate.isSameOrAfter(startMoment, 'day') && 
          targetDate.isSameOrBefore(endMoment, 'day')) {
        count++;
      }
    } catch (error) {
      // Skip years where the target week/day doesn't exist in the specified month
    }
    
    // Move to next year (gap is always 1 for yearly)
    currentYear.add(1, 'years');
  }
  
  return count;
};

//console.log('Daily example:', calculateOccurrences('2024-01-01', '2024-01-10', 'daily', 2)); // Every 2 days
//console.log('Weekly example:', calculateOccurrences('2024-01-01', '2024-01-29', 'weekly', 1)); // Every week
//console.log('Monthly example (2nd Tuesday):', calculateOccurrences('2024-01-01', '2024-06-30', 'monthly', 1, 2, 2)); // 2nd Tuesday of each month
//console.log('Yearly example (last Friday of March):', calculateOccurrences('2024-01-01', '2026-12-31', 'yearly', 1, -1, 5, 3)); // Last Friday of March each year
